import { useState, useEffect } from 'react';
import { useBooking } from '../context/BookingContext';
import { api } from '../services/api';

const CourtManagement = () => {
  const { courts, refreshConfig, settings } = useBooking();
  const [newCourtName, setNewCourtName] = useState('');
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('21:00');
  const [slotDuration, setSlotDuration] = useState('60');
  const [adminGlobalNotes, setAdminGlobalNotes] = useState('');
  const [adminGlobalImage, setAdminGlobalImage] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (settings) {
      setStartTime(settings.startTime || '07:00');
      setEndTime(settings.endTime || '21:00');
      setSlotDuration(settings.slotDuration || '60');
      setAdminGlobalNotes(settings.adminGlobalNotes || '');
      setAdminGlobalImage(settings.adminGlobalImage || '');
    }
  }, [settings]);

  const addCourt = async () => {
    if (!newCourtName.trim()) return;
    await api.addCourt(newCourtName.trim());
    setNewCourtName('');
    await refreshConfig();
    setMessage(`✅ Court "${newCourtName.trim()}" added successfully`);
    setTimeout(() => setMessage(''), 3000);
  };

  const deleteCourt = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    await api.deleteCourt(id);
    await refreshConfig();
    setMessage(`✅ "${name}" deleted successfully`);
    setTimeout(() => setMessage(''), 3000);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.updateSettings({ startTime, endTime, slotDuration, adminGlobalNotes, adminGlobalImage });
      await refreshConfig();
      setMessage('✅ Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Failed to save settings');
    }
    setSaving(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setAdminGlobalImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h2 className="text-2xl font-bold text-green-800 mb-6">Manage Courts & Settings</h2>

      {message && (
        <div className={`p-3 rounded-lg mb-4 ${message.includes('❌') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
          {message}
        </div>
      )}

      {/* Court list */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-3">Current Courts</h3>
        {courts.length === 0 ? (
          <p className="text-gray-500">No courts configured. Add one below.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {courts.map(court => (
              <li key={court.id} className="py-2 flex justify-between items-center">
                <span>{court.name}</span>
                <button
                  onClick={() => deleteCourt(court.id, court.name)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2 mt-3">
          <input
            type="text"
            placeholder="New court name"
            className="border rounded px-3 py-2 flex-1"
            value={newCourtName}
            onChange={(e) => setNewCourtName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCourt()}
          />
          <button
            onClick={addCourt}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Add Court
          </button>
        </div>
      </div>

      {/* Time Settings */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-3">Time Settings</h3>
        <p className="text-sm text-gray-500 mb-4">
          These settings control the time slots shown in the booking grid.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Time</label>
            <input
              type="time"
              className="mt-1 block w-full border rounded px-3 py-2"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Time</label>
            <input
              type="time"
              className="mt-1 block w-full border rounded px-3 py-2"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Slot Duration (minutes)</label>
            <select
              className="mt-1 block w-full border rounded px-3 py-2"
              value={slotDuration}
              onChange={(e) => setSlotDuration(e.target.value)}
            >
              <option value="30">30</option>
              <option value="45">45</option>
              <option value="60">60</option>
              <option value="90">90</option>
              <option value="120">120</option>
            </select>
          </div>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <p className="text-xs text-gray-400 mt-2">
          Changes affect the booking grid immediately for all users.
        </p>
      </div>

      {/* Global Admin Notes + Image */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-3">📝 Global Booking Notes / Rules</h3>
        <p className="text-sm text-gray-500 mb-4">
          These notes and image will be shown to all users when they confirm a booking.
        </p>

        <textarea
          className="w-full border rounded px-3 py-2 mb-4"
          rows={4}
          value={adminGlobalNotes}
          onChange={(e) => setAdminGlobalNotes(e.target.value)}
          placeholder="Add rules, payment instructions, etc."
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image (QR code, logo, etc.)</label>
          <input
            type="file"
            accept="image/*"
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            onChange={handleImageUpload}
          />
          {adminGlobalImage && (
            <div className="mt-2 flex items-center gap-3">
              <img src={adminGlobalImage} alt="Admin note image" className="h-20 w-auto rounded border" />
              <button
                onClick={() => setAdminGlobalImage('')}
                className="text-xs text-red-600 hover:underline"
              >
                Remove
              </button>
            </div>
          )}
        </div>

        <button
          onClick={saveSettings}
          disabled={saving}
          className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Global Notes'}
        </button>
      </div>
    </div>
  );
};

export default CourtManagement;